import React, { useState, useEffect } from 'react';
import { Container, Modal, Tab, Tabs, Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import { API_URL } from '../../utils/constants';
import './SetupAgentsScreen.css';

function SetupAgentsScreen() {
  const [showModal, setShowModal] = useState(true);
  const [activeTab, setActiveTab] = useState('configuration');

  // Agent Type state
  const [agentTypes, setAgentTypes] = useState([]);
  const [loadingAgentTypes, setLoadingAgentTypes] = useState(true);
  const [savingAgentType, setSavingAgentType] = useState(false);
  
  // Agent Type Modal state
  const [showAgentTypeModal, setShowAgentTypeModal] = useState(false);
  const [agentTypeModalMode, setAgentTypeModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedAgentType, setSelectedAgentType] = useState(null);
  const [agentTypeForm, setAgentTypeForm] = useState({ name: '', active: true, sort_order: 0 });
  const [agentTypeErrors, setAgentTypeErrors] = useState({});

  // Agent Commission state
  const [agentCommissions, setAgentCommissions] = useState([]);
  const [loadingAgentCommissions, setLoadingAgentCommissions] = useState(true);
  const [savingAgentCommission, setSavingAgentCommission] = useState(false);
  
  // Agent Commission Modal state
  const [showAgentCommissionModal, setShowAgentCommissionModal] = useState(false);
  const [agentCommissionModalMode, setAgentCommissionModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedAgentCommission, setSelectedAgentCommission] = useState(null);
  const [agentCommissionForm, setAgentCommissionForm] = useState({ name: '', flat_amount: 0, split_percentage: 0, active: true });
  const [agentCommissionErrors, setAgentCommissionErrors] = useState({});

  // Error and success messages
  const [message, setMessage] = useState({ text: '', variant: '', show: false });

  // Fetch data on component mount
  useEffect(() => {
    fetchAgentTypes();
    fetchAgentCommissions();
  }, []);

  // API Functions for Agent Types
  const fetchAgentTypes = async () => {
    try {
      setLoadingAgentTypes(true);
      const response = await fetch(`${API_URL}/agent-types`);
      if (!response.ok) throw new Error('Failed to fetch agent types');
      const data = await response.json();
      setAgentTypes(data);
    } catch (error) {
      console.error('Error fetching agent types:', error);
      setMessage({ text: 'Failed to load agent types', variant: 'danger', show: true });
    } finally {
      setLoadingAgentTypes(false);
    }
  };

  const updateAgentType = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/agent-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update agent type');
      }
      
      const updatedType = await response.json();
      setAgentTypes(agentTypes.map(at => at.id === id ? updatedType : at));
      setMessage({ text: 'Agent type updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating agent type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteAgentType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this agent type?')) return;
    
    try {
      const response = await fetch(`${API_URL}/agent-types/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete agent type');
      }
      
      setAgentTypes(agentTypes.filter(at => at.id !== id));
      setMessage({ text: 'Agent type deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting agent type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Agent Type Modal handlers
  const handleAddAgentType = () => {
    setAgentTypeModalMode('add');
    setSelectedAgentType(null);
    setAgentTypeForm({ name: '', active: true, sort_order: 0 });
    setAgentTypeErrors({});
    setShowAgentTypeModal(true);
  };

  const handleEditAgentType = (agentType) => {
    setAgentTypeModalMode('edit');
    setSelectedAgentType(agentType);
    setAgentTypeForm({ name: agentType.name, active: agentType.active, sort_order: agentType.sort_order || 0 });
    setAgentTypeErrors({});
    setShowAgentTypeModal(true);
  };

  const handleAgentTypeInputChange = (field, value) => {
    setAgentTypeForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (agentTypeErrors[field]) {
      setAgentTypeErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateAgentTypeForm = () => {
    const errors = {};
    if (!agentTypeForm.name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleSaveAgentType = async () => {
    const errors = validateAgentTypeForm();
    if (Object.keys(errors).length > 0) {
      setAgentTypeErrors(errors);
      return;
    }

    try {
      setSavingAgentType(true);
      if (agentTypeModalMode === 'add') {
        const response = await fetch(`${API_URL}/agent-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(agentTypeForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create agent type');
        }
        
        const newType = await response.json();
        setAgentTypes([...agentTypes, newType]);
        setMessage({ text: 'Agent type created successfully', variant: 'success', show: true });
      } else {
        await updateAgentType(selectedAgentType.id, agentTypeForm);
      }
      
      setShowAgentTypeModal(false);
      setAgentTypeForm({ name: '', active: true, sort_order: 0 });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingAgentType(false);
    }
  };

  const handleCloseAgentTypeModal = () => {
    setShowAgentTypeModal(false);
    setAgentTypeForm({ name: '', active: true, sort_order: 0 });
    setAgentTypeErrors({});
  };

  // API Functions for Agent Commissions
  const fetchAgentCommissions = async () => {
    try {
      setLoadingAgentCommissions(true);
      const response = await fetch(`${API_URL}/agent-commissions`);
      if (!response.ok) throw new Error('Failed to fetch agent commissions');
      const data = await response.json();
      setAgentCommissions(data);
    } catch (error) {
      console.error('Error fetching agent commissions:', error);
      setMessage({ text: 'Failed to load agent commissions', variant: 'danger', show: true });
    } finally {
      setLoadingAgentCommissions(false);
    }
  };

  const updateAgentCommission = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/agent-commissions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update agent commission');
      }
      
      const updatedCommission = await response.json();
      setAgentCommissions(agentCommissions.map(ac => ac.id === id ? updatedCommission : ac));
      setMessage({ text: 'Agent commission updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating agent commission:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteAgentCommission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this agent commission?')) return;
    
    try {
      const response = await fetch(`${API_URL}/agent-commissions/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete agent commission');
      }
      
      setAgentCommissions(agentCommissions.filter(ac => ac.id !== id));
      setMessage({ text: 'Agent commission deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting agent commission:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Agent Commission Modal handlers
  const handleAddAgentCommission = () => {
    setAgentCommissionModalMode('add');
    setSelectedAgentCommission(null);
    setAgentCommissionForm({ name: '', flat_amount: 0, split_percentage: 0, active: true });
    setAgentCommissionErrors({});
    setShowAgentCommissionModal(true);
  };

  const handleEditAgentCommission = (agentCommission) => {
    setAgentCommissionModalMode('edit');
    setSelectedAgentCommission(agentCommission);
    setAgentCommissionForm({ 
      name: agentCommission.name, 
      flat_amount: agentCommission.flat_amount || 0, 
      split_percentage: agentCommission.split_percentage || 0, 
      active: agentCommission.active 
    });
    setAgentCommissionErrors({});
    setShowAgentCommissionModal(true);
  };

  const handleAgentCommissionInputChange = (field, value) => {
    setAgentCommissionForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (agentCommissionErrors[field]) {
      setAgentCommissionErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateAgentCommissionForm = () => {
    const errors = {};
    if (!agentCommissionForm.name.trim()) {
      errors.name = 'Name is required';
    }
    if (agentCommissionForm.split_percentage < 0 || agentCommissionForm.split_percentage > 100) {
      errors.split_percentage = 'Split percentage must be between 0 and 100';
    }
    return errors;
  };

  const handleSaveAgentCommission = async () => {
    const errors = validateAgentCommissionForm();
    if (Object.keys(errors).length > 0) {
      setAgentCommissionErrors(errors);
      return;
    }

    try {
      setSavingAgentCommission(true);
      if (agentCommissionModalMode === 'add') {
        const response = await fetch(`${API_URL}/agent-commissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(agentCommissionForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create agent commission');
        }
        
        const newCommission = await response.json();
        setAgentCommissions([...agentCommissions, newCommission]);
        setMessage({ text: 'Agent commission created successfully', variant: 'success', show: true });
      } else {
        await updateAgentCommission(selectedAgentCommission.id, agentCommissionForm);
      }
      
      setShowAgentCommissionModal(false);
      setAgentCommissionForm({ name: '', flat_amount: 0, split_percentage: 0, active: true });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingAgentCommission(false);
    }
  };

  const handleCloseAgentCommissionModal = () => {
    setShowAgentCommissionModal(false);
    setAgentCommissionForm({ name: '', flat_amount: 0, split_percentage: 0, active: true });
    setAgentCommissionErrors({});
  };

  const handleCloseModal = () => {
    // Modal can only be closed by clicking the X button or specific buttons
    // This prevents accidental closing
  };

  const handleModalClose = () => {
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSaveAndClose = () => {
    // Save any changes and close modal
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSave = () => {
    // Save any changes but keep modal open
    console.log('Saving changes...');
  };

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message.show) {
      const timer = setTimeout(() => {
        setMessage({ text: '', variant: '', show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message.show]);

  return (
    <Container fluid className="setup-agents-screen">
      <div className="screen-header">
        <h1>Setup - Agents</h1>
        <p>Configure agent settings and management</p>
      </div>

      <div className="screen-content">
        {/* Message Alert */}
        {message.show && (
          <Alert 
            variant={message.variant} 
            dismissible 
            onClose={() => setMessage({ text: '', variant: '', show: false })}
            className="mb-3"
          >
            {message.text}
          </Alert>
        )}

        {/* Main Setup Modal */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="xl"
          backdrop="static"
          keyboard={false}
          className="setup-modal"
        >
          <Modal.Header>
            <Modal.Title>Setup - Agents</Modal.Title>
            <Button
              variant="link"
              onClick={handleModalClose}
              className="close-button"
            >
              ×
            </Button>
          </Modal.Header>
          
          <Modal.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="setup-tabs"
            >
              <Tab eventKey="configuration" title="Configuration">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Configuration</h4>
                    <p>General agent configuration settings will be added here.</p>
                  </div>
                </div>
              </Tab>

              <Tab eventKey="agent-type" title="Agent Type">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Agent Types</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddAgentType}
                      size="sm"
                    >
                      ➕ Add Agent Type
                    </Button>
                  </div>
                  
                  {loadingAgentTypes ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Sort Order</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentTypes.map((at) => (
                            <tr key={at.id}>
                              <td>{at.name}</td>
                              <td>{at.sort_order || 0}</td>
                              <td>
                                <span className={`badge ${at.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {at.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditAgentType(at)}
                                  className="me-2"
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => deleteAgentType(at.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="agent-commission" title="Agent Commission">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Agent Commissions</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddAgentCommission}
                      size="sm"
                    >
                      ➕ Add Agent Commission
                    </Button>
                  </div>
                  
                  {loadingAgentCommissions ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Flat Amount</th>
                            <th>Split Percentage</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentCommissions.map((ac) => (
                            <tr key={ac.id}>
                              <td>{ac.name}</td>
                              <td>${ac.flat_amount || 0}</td>
                              <td>{ac.split_percentage || 0}%</td>
                              <td>
                                <span className={`badge ${ac.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {ac.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditAgentCommission(ac)}
                                  className="me-2"
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => deleteAgentCommission(ac.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSaveAndClose}>
              Save & Close
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleModalClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Agent Type Modal */}
        <Modal 
          show={showAgentTypeModal} 
          onHide={handleCloseAgentTypeModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {agentTypeModalMode === 'add' ? 'Add Agent Type' : 'Edit Agent Type'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={agentTypeForm.name}
                  onChange={(e) => handleAgentTypeInputChange('name', e.target.value)}
                  isInvalid={!!agentTypeErrors.name}
                  placeholder="e.g., Sales Agent, Booking Agent, Manager"
                />
                <Form.Control.Feedback type="invalid">
                  {agentTypeErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={agentTypeForm.sort_order}
                  onChange={(e) => handleAgentTypeInputChange('sort_order', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={agentTypeForm.active}
                  onChange={(e) => handleAgentTypeInputChange('active', e.target.checked)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAgentTypeModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveAgentType}
              disabled={savingAgentType}
            >
              {savingAgentType ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Agent Commission Modal */}
        <Modal 
          show={showAgentCommissionModal} 
          onHide={handleCloseAgentCommissionModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {agentCommissionModalMode === 'add' ? 'Add Agent Commission' : 'Edit Agent Commission'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={agentCommissionForm.name}
                  onChange={(e) => handleAgentCommissionInputChange('name', e.target.value)}
                  isInvalid={!!agentCommissionErrors.name}
                  placeholder="e.g., Standard Commission, Premium Commission"
                />
                <Form.Control.Feedback type="invalid">
                  {agentCommissionErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Flat Amount ($)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={agentCommissionForm.flat_amount}
                  onChange={(e) => handleAgentCommissionInputChange('flat_amount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Split Percentage (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={agentCommissionForm.split_percentage}
                  onChange={(e) => handleAgentCommissionInputChange('split_percentage', parseFloat(e.target.value) || 0)}
                  isInvalid={!!agentCommissionErrors.split_percentage}
                  placeholder="0.00"
                />
                <Form.Control.Feedback type="invalid">
                  {agentCommissionErrors.split_percentage}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Enter a value between 0 and 100
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={agentCommissionForm.active}
                  onChange={(e) => handleAgentCommissionInputChange('active', e.target.checked)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAgentCommissionModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveAgentCommission}
              disabled={savingAgentCommission}
            >
              {savingAgentCommission ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
}

export default SetupAgentsScreen;
